import { useEffect, useState } from 'react';
import { createLogger } from '../logger';

export const useRecaptcha = () => {
	const logger = createLogger('Recaptcha');
	const [isLoaded, setIsLoaded] = useState(false);

	const loadRecaptcha = (): Promise<void> => {
		return new Promise((resolve, reject) => {
			if (window.grecaptcha) {
				setIsLoaded(true);
				resolve();
				return;
			}

			const scriptId = 'recaptcha-enterprise';
			const existingScript = document.getElementById(scriptId);

			if (existingScript) {
				existingScript.addEventListener('load', () => {
					setIsLoaded(true);
					resolve();
				});
				return;
			}

			const script = document.createElement('script');
			script.id = scriptId;
			script.src = `https://www.google.com/recaptcha/enterprise.js?render=${import.meta.env.VITE_RECAPTCHA_SITE_KEY}`;
			script.async = true;
			script.defer = true;

			script.onload = () => {
				setIsLoaded(true);
				logger.debug('reCaptcha Ready');
				resolve();
			};

			script.onerror = (err) => {
				logger.error('Failed to load reCAPTCHA script');
				reject(err);
			};

			document.body.appendChild(script);
		});
	};

	useEffect(() => {
		loadRecaptcha().catch((err) => logger.error(err));
	}, []);

	const execute = async (action: string): Promise<string> => {
		if (!window.grecaptcha || !isLoaded) {
			try {
				await loadRecaptcha();
			} catch (err) {
				logger.error('reCAPTCHA failed to load', err);
				return '';
			}
		}

		try {
			return await window.grecaptcha.enterprise.execute(import.meta.env.VITE_RECAPTCHA_SITE_KEY, { action });
		} catch (err) {
			logger.error('reCAPTCHA execution failed', err);
			return '';
		}
	};

	return { isLoaded, execute };
};
